// client/src/components/pages/LexiStar/FuelConfig/FuelRowDialog.tsx (converted from .jsx, ST-00204)
//
// Create a fuel configuration row, or change one. A change never edits the row:
// the server closes it at "Takes effect" (default now) and opens a new row with
// the new values from that instant, so trips already dispatched keep what they
// were given (LS-04 §12).
//
// ST-00204: "Takes effect" is the shared UnifiedDatePicker — on a desktop a field opening the
// date + time popup under it (ST-00294), on a phone a button opening the date + time sheet (the
// old react-datepicker popup opened above the dialog, off the top of a phone screen).

import { useEffect, useState, type ComponentType, type FormEvent, type ReactNode } from 'react';
import { FaTimes, FaPlus } from 'react-icons/fa';
import UnifiedDatePicker from '../../../common/UnifiedDatePicker';
import ThemedSelectJs from '../../../common/ThemedSelect';
// Types only: the facade module also loads the map, which this dialog does not need.
import type { ThemedSelectProps } from '../../../../types/commonComponents';
import { formatAppDateTime } from '../../../../utils/dateUtils';
import {
  KINDS,
  emptyForm as emptyFormJs,
  validateForm as validateFormJs,
  toPayload as toPayloadJs,
  changesSomething,
  normSubject as normSubjectJs,
} from './fuelConfig';

export type FuelKind = 'norm' | 'price' | 'rateCard';
export type FuelDialogMode = 'create' | 'change';

interface RouteFactorRow {
  route_id: string;
  factor: string | number;
}

/** The dialog's form (fuelConfig.emptyForm); the fields in use depend on the kind. */
interface FuelForm {
  scope?: string;
  vehicle_class?: string;
  asset_id?: string | number;
  load_state?: string;
  l_per_100km?: string | number;
  idle_lph?: string | number;
  route_factor?: RouteFactorRow[];
  region?: string;
  price_per_l?: string | number;
  rate_per_km?: string | number;
  driver_rate_per_h?: string | number;
  toll_class?: string;
  effective_from: Date | null;
}
type ScalarField = Exclude<keyof FuelForm, 'route_factor' | 'effective_from'>;

/** A norm / price / rate-card row as the fuel-config API lists it. */
export interface FuelRow {
  scope?: string;
  vehicle_class?: string;
  asset_id?: string | number;
  asset_registration?: string | null;
  load_state?: string;
  region?: string;
  effective_from: string;
  [key: string]: unknown;
}

export interface FuelRowDialogProps {
  isOpen: boolean;
  kind: FuelKind;
  mode: FuelDialogMode;
  row: FuelRow | null;
  vehicleClasses?: string[];
  assets?: Array<{ id: string | number; registration: string; fleet_no?: string | null }>;
  routes?: Array<{ id: string | number; code?: string | null; name?: string | null }>;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<unknown> | void;
}

// fuelConfig.js has no types of its own; these are the shapes it works with.
const emptyForm = emptyFormJs as unknown as (kind: FuelKind, row?: FuelRow | null) => FuelForm;
const validateForm = validateFormJs as unknown as (
  kind: FuelKind,
  mode: FuelDialogMode,
  form: FuelForm,
  row?: FuelRow | null
) => string | null;
const toPayload = toPayloadJs as unknown as (
  kind: FuelKind,
  mode: FuelDialogMode,
  form: FuelForm,
  row?: FuelRow | null
) => Record<string, unknown>;
const normSubject = normSubjectJs as unknown as (row: FuelRow) => string;

const ThemedSelect = ThemedSelectJs as unknown as ComponentType<ThemedSelectProps>;

const inputClass =
  'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-slate-50';

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

const apiError = (err: unknown): string => {
  const data = (err as { response?: { data?: { error?: string; details?: string[] } } })?.response
    ?.data;
  return data?.details?.length
    ? `${data.error}: ${data.details.join('; ')}`
    : data?.error || 'Could not save.';
};

export default function FuelRowDialog({
  isOpen,
  kind,
  mode, // 'create' | 'change'
  row,
  vehicleClasses = [],
  assets = [],
  routes = [],
  onClose,
  onSubmit,
}: FuelRowDialogProps) {
  const [form, setForm] = useState<FuelForm>(() => emptyForm(kind, row));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(emptyForm(kind, mode === 'change' ? row : null));
      setError(null);
    }
  }, [isOpen, kind, mode, row]);

  if (!isOpen) return null;

  const set = (patch: Partial<FuelForm>) => setForm((f) => ({ ...f, ...patch }));
  const bind = (k: ScalarField) => ({
    value: form[k] ?? '',
    onChange: (e: { target: { value: string } }) => set({ [k]: e.target.value }),
  });
  const creating = mode === 'create';
  const title = `${creating ? 'New' : 'Change'} ${KINDS[kind].label.toLowerCase()}`;
  const routeFactors = form.route_factor || [];

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const problem = validateForm(kind, mode, form, row);
    if (problem) return setError(problem);
    const payload = toPayload(kind, mode, form, row);
    if (!creating && !changesSomething(payload)) return setError('Nothing changed.');
    setSaving(true);
    setError(null);
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <form
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={submit}
        className="bg-surface rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 pt-5">
          <h3 className="text-lg font-bold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <FaTimes />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          {!creating && row && (
            <p className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              {kind === 'norm'
                ? `${normSubject(row)}, ${row.load_state}`
                : kind === 'price'
                  ? `Region ${row.region}`
                  : `Class ${row.vehicle_class}`}{' '}
              — in force since {formatAppDateTime(row.effective_from)}. The current row ends when
              this change takes effect.
            </p>
          )}

          {kind === 'norm' && creating && (
            <>
              <Field label="Applies to" htmlFor="norm-scope">
                <ThemedSelect
                  id="norm-scope"
                  value={form.scope ?? ''}
                  onChange={(e) => set({ scope: String(e.target.value) })}
                >
                  <option value="vehicle_class">A vehicle class</option>
                  <option value="asset">One asset (overrides its class)</option>
                </ThemedSelect>
              </Field>
              {form.scope === 'vehicle_class' ? (
                <Field label="Vehicle class" htmlFor="norm-class">
                  <input
                    id="norm-class"
                    list="fuel-vehicle-classes"
                    className={inputClass}
                    maxLength={40}
                    {...bind('vehicle_class')}
                  />
                </Field>
              ) : (
                <Field label="Asset" htmlFor="norm-asset">
                  <ThemedSelect
                    id="norm-asset"
                    value={String(form.asset_id ?? '')}
                    onChange={(e) => set({ asset_id: String(e.target.value) })}
                  >
                    <option value="">
                      {assets.length ? 'Choose an asset…' : 'No assets registered'}
                    </option>
                    {assets.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.registration}
                        {a.fleet_no ? ` (${a.fleet_no})` : ''}
                      </option>
                    ))}
                  </ThemedSelect>
                </Field>
              )}
              <Field label="Load state" htmlFor="norm-load">
                <ThemedSelect
                  id="norm-load"
                  value={form.load_state ?? ''}
                  onChange={(e) => set({ load_state: String(e.target.value) })}
                >
                  <option value="loaded">Loaded</option>
                  <option value="empty">Empty</option>
                </ThemedSelect>
              </Field>
            </>
          )}

          {kind === 'norm' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Litres per 100 km" htmlFor="norm-l100">
                  <input
                    id="norm-l100"
                    inputMode="decimal"
                    className={inputClass}
                    {...bind('l_per_100km')}
                  />
                </Field>
                <Field label="Idle litres per hour" htmlFor="norm-idle">
                  <input
                    id="norm-idle"
                    inputMode="decimal"
                    className={inputClass}
                    placeholder="e.g. 3"
                    {...bind('idle_lph')}
                  />
                </Field>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-slate-700">Route factors</legend>
                <p className="text-xs text-slate-500">
                  Multiply the norm on a hilly or rough route (1.10 = 10 % more).
                </p>
                {routeFactors.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <ThemedSelect
                        id={`norm-rf-route-${i}`}
                        aria-label={`Route factor ${i + 1} route`}
                        value={f.route_id}
                        onChange={(e) =>
                          set({
                            route_factor: routeFactors.map((x, j) =>
                              j === i ? { ...x, route_id: String(e.target.value) } : x
                            ),
                          })
                        }
                      >
                        <option value="">Choose a route…</option>
                        {routes.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.code} — {r.name}
                          </option>
                        ))}
                      </ThemedSelect>
                    </div>
                    <input
                      aria-label={`Route factor ${i + 1}`}
                      inputMode="decimal"
                      className="w-20 px-2 py-2 border border-slate-200 rounded-lg text-sm"
                      value={f.factor}
                      onChange={(e) =>
                        set({
                          route_factor: routeFactors.map((x, j) =>
                            j === i ? { ...x, factor: e.target.value } : x
                          ),
                        })
                      }
                    />
                    <button
                      type="button"
                      onClick={() => set({ route_factor: routeFactors.filter((_, j) => j !== i) })}
                      aria-label={`Remove route factor ${i + 1}`}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
                {routes.length > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      set({ route_factor: [...routeFactors, { route_id: '', factor: '1.1' }] })
                    }
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                  >
                    <FaPlus size={10} aria-hidden="true" /> Add a route factor
                  </button>
                )}
              </fieldset>
            </>
          )}

          {kind === 'price' && (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Region"
                htmlFor="price-region"
                hint={creating ? 'e.g. ZA, ZA-coastal, ZA-inland' : undefined}
              >
                <input
                  id="price-region"
                  className={inputClass}
                  maxLength={40}
                  disabled={!creating}
                  {...bind('region')}
                />
              </Field>
              <Field label="Price per litre (R)" htmlFor="price-l">
                <input
                  id="price-l"
                  inputMode="decimal"
                  className={inputClass}
                  {...bind('price_per_l')}
                />
              </Field>
            </div>
          )}

          {kind === 'rateCard' && (
            <>
              <Field label="Vehicle class" htmlFor="rc-class">
                <input
                  id="rc-class"
                  list="fuel-vehicle-classes"
                  className={inputClass}
                  maxLength={40}
                  disabled={!creating}
                  {...bind('vehicle_class')}
                />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Rate per km (R)" htmlFor="rc-km">
                  <input
                    id="rc-km"
                    inputMode="decimal"
                    className={inputClass}
                    {...bind('rate_per_km')}
                  />
                </Field>
                <Field label="Driver per hour (R)" htmlFor="rc-driver">
                  <input
                    id="rc-driver"
                    inputMode="decimal"
                    className={inputClass}
                    {...bind('driver_rate_per_h')}
                  />
                </Field>
                <Field label="Toll class" htmlFor="rc-toll">
                  <input
                    id="rc-toll"
                    className={inputClass}
                    maxLength={20}
                    placeholder="e.g. 4"
                    {...bind('toll_class')}
                  />
                </Field>
              </div>
            </>
          )}

          <Field
            label="Takes effect"
            htmlFor="fuel-effective-from"
            hint={
              creating
                ? 'Leave empty to start now. A future date schedules it.'
                : 'Leave empty to change it now. Must be after the current row started.'
            }
          >
            <UnifiedDatePicker
              id="fuel-effective-from"
              value={form.effective_from}
              onChange={(d) => set({ effective_from: d })}
              withTime
              timeIntervals={15}
              dateFormat="yyyy/MM/dd HH:mm"
              placeholder="Now"
              clearable
              className={inputClass}
              wrapperClassName="w-full"
              title="Takes effect"
            />
          </Field>

          <datalist id="fuel-vehicle-classes">
            {vehicleClasses.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>

          {error && (
            <p role="alert" className="text-sm text-red-700">
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-60"
          >
            {saving ? 'Saving…' : creating ? 'Add' : 'Save change'}
          </button>
        </div>
      </form>
    </div>
  );
}
